import type { SuperHubStore } from '..';
import type { EphemeralBranch } from './utils';
import type {
  ReviewReportFileSection,
  ReviewReportFullResponseType,
  ReviewReportResponseType,
} from '@readme/api/src/mappings/branch/reviewReport/types';
import type { ReadBranchReviewStatusType } from '@readme/api/src/mappings/branch/reviewStatus/types';
import type { ReadBranchCollectionType, ReadBranchType } from '@readme/api/src/mappings/branch/types';
import type { ReadMergedBranchType, ReadMergeType } from '@readme/api/src/mappings/merge/types';
import type { CreateBranchType, VersionRepresentationType } from '@readme/api/src/mappings/version/types';
import type { BranchDiffPage, BranchDiffSection, BranchDiffStatus } from '@readme/iso/src/branches/diff';
import type { SetOptional, WritableDeep } from 'type-fest';
import type { StateCreator } from 'zustand';

import { findDiffPageBySlug, parseVersionBranch } from '@readme/iso';
import produce from 'immer';

import { mutate } from '@Hub/swr';

import type useReadmeApi from '@core/hooks/useReadmeApi';
import { fetcher } from '@core/hooks/useReadmeApi';
import { actionLog, isClient } from '@core/store/util';
import type { APIErrorType } from '@core/types';

import { isBranch } from '../util';

import {
  createEphemeralBranch,
  reduceDiffStatusData,
  reviewReportSectionToBranchDiffSection,
  routeToBranchDiffSection,
} from './utils';

/** Per-section branch diff data that contains the tree of changed files. */
export interface BranchDiffStatusData {
  /** Expanded diff data for a particular branch's section. */
  data: BranchDiffStatus | null;

  /** Contains error info that occurred when hydrating this section. */
  error: APIErrorType | null;

  /** Whether this section is currently being fetched via API. */
  isLoading: boolean;

  /**
   * Holds references to the SWR request keys used to fetch this resource from
   * our API. This key is used by SWR's `mutate()` function to update the SWR
   * cache after a mutation request returns successful.
   * @link https://swr.vercel.app/docs/mutation
   * @example
   * ```ts
   * ['/cats/api-next/v2/branches/2.0_my-branch/merge', {}]
   * ```
   */
  swrKey?: ReturnType<typeof useReadmeApi>['swrKey'];
}

export type BranchDiffStatusMap = Record<BranchDiffSection, BranchDiffStatusData>;

interface VersionBranchesData {
  /**
   * The collection of branches for this version, or `null` if branches for that
   * version are currently being fetched.
   */
  branches: ReadBranchCollectionType | null;

  /**
   * Contains error information that occurred while hydrating this version's
   * child branches.
   */
  error: APIErrorType | null;

  /**
   * Whether this version's branches data is being fetched via API.
   */
  isLoading: boolean;

  /**
   * Holds references to the SWR request keys that is used to fetch this
   * version's branches data from our API. This key is used by SWR's `mutate()`
   * function to update the SWR cache after a mutation request returns
   * successful.
   * @link https://swr.vercel.app/docs/mutation
   * @example
   * ```ts
   * ['/cats/api-next/v2/branches/2.0_my-branch', {}]
   * ```
   */
  swrKey?: ReturnType<typeof useReadmeApi>['swrKey'];
}

interface SuperHubBranchesSliceState {
  /**
   * `true` if the current user has the necessary permissions to merge branches.
   */
  canUserMerge: boolean;

  /**
   * Map of branch diff statuses per section. This is where we store diff data
   * as they've been fetched from our branch's merge status endpoint.
   */
  diffStatusMap: Partial<BranchDiffStatusMap>;

  /**
   * Indicates whether we should be in "creation" mode by being assigned a newly
   * forked branch or `null`. Use `updateEphemeralBranch()` to update this.
   */
  ephemeralBranch: EphemeralBranch | null;

  /**
   * Contains error information that occurred during hydration or updates.
   */
  error: APIErrorType | null;

  /**
   * Indicates this slice has been initialized on both server and client. We use
   * this to differentiate how the state should update during SSR vs CSR. On the
   * server, the state should never prevent updates or retain previously
   * existing data. This is only safe to do once this flag is flipped to `true`.
   * @see initialize
   */
  isReady: boolean;

  /**
   * Contains the current branch's most recently created review report. This is
   * populated after the current branch is marked "ready" for review for the
   * first time or when manually triggered via `createReviewReport()`. Defaults
   * to `null` to indicate no report yet exists.
   */
  reviewReport: ReviewReportFullResponseType['data'] | null;

  /**
   * A map of versions to data about the a paginated collection of that
   * version's child branches.
   */
  versionsMap: Record<VersionRepresentationType['name'], VersionBranchesData | undefined>;
}

interface SuperHubBranchSliceAction {
  /**
   * Creates a new branch that is forked from a version.
   */
  createBranch: (newBranch: Pick<CreateBranchType, 'name'>) => Promise<ReadBranchType>;

  /**
   * Triggers a new review report job for the current branch. This is typically
   * called when after a branch is marked "ready" for review or when we need to
   * manually trigger a re-run of the report.
   */
  createReviewReport: () => Promise<ReviewReportResponseType>;

  /**
   * Deletes a branch.
   */
  deleteBranch: (name: ReadBranchType['data']['name']) => Promise<void>;

  /**
   * Find the diff page from within our `diffStatusMap` based on the current
   * route section and slug by default. Use `options` to override this and
   * lookup a different section or slug. Returns `null` if none is found.
   */
  getDiffPage: (options?: {
    section?: SuperHubStore['routeSection'];
    slug?: SuperHubStore['slug'];
  }) => BranchDiffPage | null;

  /**
   * Gets the merge status for an existing branch. Indicates whether a merge
   * conflict exists along with additional diff patches info.
   */
  getMergeStatus: (name: ReadBranchType['data']['name']) => Promise<ReadMergeType>;

  /**
   * Find the file from with our `reviewReport` that matches the current
   * route section and slug. Use `options` to override this and lookup a
   * different section or slug. Returns `null` if none is found.
   */
  getReviewReportFile: (options?: {
    section?: SuperHubStore['routeSection'];
    slug?: SuperHubStore['slug'];
  }) => ReviewReportFullResponseType['data']['files'][number] | null;

  /**
   * Updates this state slice with the provided branches data and error. The
   * entries in the provided `versionsMap` are added alongside existing entries
   * and will not overwrite any entries for other keys.
   *
   * For example, with existing state:
   * ```ts
   * { '1.0': { branches: [{ name: '1.0_branch' }] } }
   * ```
   *
   * This call:
   * ```ts
   * initialize({ versionsMap: { '2.0': { branches: [{ name: '2.0_branch' }] } } })
   * ```
   *
   * Will result in a final state of:
   * ```ts
   * {
   *   '1.0': { branches: [{ name: '1.0_branch' }] },
   *   '2.0': { branches: [{ name: '2.0_branch' }] }
   * }
   * ```
   */
  initialize: (payload: {
    canUserMerge?: boolean;
    error?: APIErrorType | null;
    versionsMap?: Record<VersionRepresentationType['name'], SetOptional<VersionBranchesData, 'error'>>;
  }) => void;

  /**
   * Returns `true` if the current review report is stale and new changes have
   * been committed to the branch since the report run. If no argument is
   * provided, it will use the value in `store.branches.reviewReport`.
   */
  isReviewReportStale: (reviewReport?: ReviewReportFullResponseType['data'] | null) => boolean;

  /**
   * Merges an existing branch into its base version.
   */
  mergeBranch: (
    name: ReadBranchType['data']['name'],
    options?: { cleanupBranch: boolean },
  ) => Promise<ReadMergedBranchType>;

  /**
   * Renames an existing branch to a different name.
   */
  renameBranch: (name: ReadBranchType['data']['name'], newName: string) => Promise<ReadBranchType>;

  /**
   * Revalidates branches data for a given version by refetching data from our
   * API endpoint.
   */
  revalidate: (versionName: string) => Promise<ReadBranchCollectionType | undefined>;

  /**
   * Revalidates data for a the singluar current branch by refetching data from our
   * API endpoint.
   */
  revalidateCurrentBranch: () => Promise<ReadBranchType | undefined>;

  /**
   * Adds or updates entries into our diff status data map. Some sections
   * are normalized and segmented into additional sections within the map.
   *
   * 1. `reference` => {
   *   `reference`, // API endpoints
   *   `apiDefinitions`, // OAS definitions
   * }
   * 2. `customContent` => {
   *   `customContent`, // Reusable content
   *   `customComponents`, // MDX components
   * }
   */
  updateDiffStatusMap: (diffStatusMap: SuperHubBranchesSliceState['diffStatusMap']) => void;

  /**
   * Updates `ephemeralBranch` to enter "creation" mode by assigning a new
   * branch that's been forked from the provided version. Passing in `null`
   * clears the field and exits "creation" mode.
   * @example
   * ```ts
   * superHubStore.branches.updateEphemeralBranch('2.0'); // Fork from version 2.0
   * superHubStore.branches.updateEphemeralBranch(null);  // Clear ephemeral branch
   * ```
   */
  updateEphemeralBranch: (ephemeralBranch: Pick<EphemeralBranch, 'base' | 'source'> | null) => void;

  /**
   * Updates the current branch's review report. This does not send any requests
   * to our API and is only used to update our local store.
   */
  updateReviewReport: (reviewReport: ReviewReportFullResponseType['data']) => Promise<void>;

  /**
   * Update a branch's review status. By default, the `currentBranch` is acted
   * upon but can be overridden by providing a `branch` argument.
   */
  updateReviewStatus: (options: {
    /** Optional branch name to update. Defaults to `currentBranch.name`. */
    branch?: ReadBranchType['data']['name'];
    status: ReadBranchReviewStatusType['data']['status'];
  }) => Promise<ReadBranchReviewStatusType>;
}

export interface SuperHubBranchesSlice {
  /**
   * State slice containing child branches data for the current project and
   * actions that can be performed against them.
   */
  branches: SuperHubBranchesSliceState & SuperHubBranchSliceAction;
}

const initialState: SuperHubBranchesSliceState = {
  canUserMerge: false,
  ephemeralBranch: null,
  error: null,
  isReady: false,
  diffStatusMap: {},
  reviewReport: null,
  versionsMap: {},
};

/**
 * Versions state slice containing all things related to project branches.
 */
export const createSuperHubBranchesSlice: StateCreator<
  SuperHubBranchesSlice & SuperHubStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  SuperHubBranchesSlice
> = (set, get) => ({
  branches: {
    ...initialState,

    createBranch: async newBranch => {
      const baseVersionName = parseVersionBranch(newBranch.name).version;

      set(
        state => {
          /**
           * If we're creating a branch for a version that hasn't been loaded
           * yet, there is nothing we need to optimistically update. The new
           * branch will get returned from the API whenever that version loads
           * its branches list.
           */
          const versionBranchesData = state.branches.versionsMap[baseVersionName]?.branches;
          const baseVersion = state.versions.data.data.find(version => version.name === baseVersionName);
          if (!versionBranchesData || !baseVersion) {
            return;
          }

          const now = new Date().toISOString();
          versionBranchesData.data.push({
            base: baseVersion,
            href: { external: { diff: null, view: null } },
            name: newBranch.name,
            review: {
              branch: {
                name: newBranch.name,
                uri: `/branches/${encodeURIComponent(newBranch.name)}`,
              },
              contributors: [],
              ready_at: null,
              ready_by: { email: null, name: null },
              report: { id: null },
              reviewers: [],
              status: 'draft',
              updated_at: now,
            },
            updated_at: now,
            uri: `/branches/${encodeURIComponent(newBranch.name)}`,
          });
          versionBranchesData.total += 1;
        },
        false,
        actionLog('branches.createBranch', newBranch),
      );

      const request = fetcher<ReadBranchType>(get().getApiBaseUrl({ branch: '' }), {
        method: 'POST',
        body: JSON.stringify(newBranch),
      });

      const {
        branches: { versionsMap },
      } = get();

      await mutate(
        versionsMap[baseVersionName]?.swrKey,
        request.then(({ data }) =>
          produce(versionsMap[baseVersionName]?.branches, draft => {
            if (draft) {
              // if we were able to optimistically appended the new branch while
              // the request was in flight at the start of this action, update
              // that appended value to have the data returned in the response
              if (draft.data[draft.data.length - 1]?.name === newBranch.name) {
                draft.data[draft.data.length - 1] = data;
              }
              // else if we weren't able to perform that optimistic append, do
              // it now that we have the data returned in the response
              else {
                draft.data.push(data);
                draft.total += 1;
              }
            }
          }),
        ),
        { revalidate: true },
      );

      // Clear ephemeral version after successful creation.
      get().branches.updateEphemeralBranch(null);

      return request;
    },

    createReviewReport: async () => {
      const {
        branches: { versionsMap },
      } = get();

      const currentBranch = get().getCurrentChildBranch();
      if (!currentBranch) {
        throw new Error('Cannot create review report: no current branch');
      }

      const request = fetcher<ReviewReportResponseType>(`${get().getApiBaseUrl()}/review/reports`, {
        // Fastify requires request bodies on POST requests 😑
        body: '{}',
        method: 'POST',
      });

      // Find current branch in versions map and update its review report.
      await mutate(
        versionsMap[currentBranch.base.name]?.swrKey,
        request.then(({ data: reviewReport }) => {
          set(
            state => {
              // Do nothing when not on a child branch.
              if (!isBranch(state.currentBranch)) return;

              // Update the current branch's review report.
              state.branches.reviewReport = { ...reviewReport, files: [] };

              // Update the current branch's review status report.
              if (state.currentBranch.review) {
                state.currentBranch.review.report.id = reviewReport.id;
              }
            },
            false,
            actionLog('branches.createReviewReport'),
          );

          return produce(versionsMap[currentBranch.base.name]?.branches, draft => {
            const foundBranch = draft?.data.find(b => b.name === currentBranch.name);
            if (foundBranch?.review) {
              foundBranch.review.report.id = reviewReport.id;
            }
          });
        }),
        { revalidate: false },
      );

      return request;
    },

    deleteBranch: async name => {
      const request = fetcher<void>(get().getApiBaseUrl({ branch: name }), {
        method: 'DELETE',
      });

      const { version } = parseVersionBranch(name);

      const {
        branches: { versionsMap },
      } = get();

      await mutate(
        versionsMap[version]?.swrKey,
        request.then(() =>
          produce(versionsMap[version]?.branches, draft => {
            if (draft) {
              const indexToRemove = draft.data.findIndex(v => v.name === name);
              if (indexToRemove > -1) {
                draft.data.splice(indexToRemove, 1);
                draft.total -= 1;
              }
            }
          }),
        ),
        { revalidate: false },
      );

      return request;
    },

    getDiffPage: ({ section = get().routeSection, slug = get().slug } = {}) => {
      if (!section || !slug) return null;

      const diffSection = routeToBranchDiffSection[section];
      if (!diffSection) return null;

      const diffStatus = get().branches.diffStatusMap[diffSection];
      if (!diffStatus || !diffStatus.data) return null;

      return findDiffPageBySlug(diffStatus.data.sidebar, slug);
    },

    getMergeStatus: async name => {
      return fetcher<ReadMergeType>(`${get().getApiBaseUrl({ branch: name })}/merge`, {
        method: 'GET',
      });
    },

    getReviewReportFile: ({ section = get().routeSection, slug = get().slug } = {}) => {
      if (!section || !slug) return null;

      const reviewReport = get().branches.reviewReport;
      if (!reviewReport || !reviewReport.files) return null;

      return reviewReport.files.find(data => data.file.section === section && data.file.slug === slug) || null;
    },

    mergeBranch: async (name, options) => {
      const { cleanupBranch } = options || {};
      const { version } = parseVersionBranch(name);

      const request = fetcher<ReadMergedBranchType>(
        `${get().getApiBaseUrl({ branch: name })}/merge${cleanupBranch ? '?cleanup_branch=true' : ''}`,
        {
          // Fastify requires request bodies on PUT requests 😑
          body: '{}',
          method: 'PUT',
        },
      );

      const {
        branches: { versionsMap },
      } = get();

      await mutate(
        versionsMap[version]?.swrKey,
        request.then(() =>
          produce(versionsMap[version]?.branches, draft => {
            if (draft) {
              const mergedBranchIndex = draft.data.findIndex(v => v.name === name);
              if (mergedBranchIndex > -1) {
                // Response will eventually include an `updated_at` timestamp
                // for us to use here. But until then, simply use local time.
                draft.data[mergedBranchIndex].updated_at = new Date().toISOString();

                // if the cleanupBranch option was provided, remove the branch
                // from the store
                if (cleanupBranch) {
                  draft.data.splice(mergedBranchIndex, 1);
                  draft.total -= 1;
                }
              }
            }
          }),
        ),
        { revalidate: false },
      );

      return request;
    },

    initialize: ({ versionsMap = {}, error = null, canUserMerge = false }) => {
      const prevVersionsMap = get().branches.versionsMap;
      /**
       * For each entry in the provided `versionsMap`, if its `branches` value
       * is `null` but we have previously fetched branches for that version,
       * preserve the existing branches. This allows us to not lose existing
       * data while a new fetch is in progress.
       *
       * Note that future versions of SWR make this easier:
       * https://swr.vercel.app/docs/advanced/understanding#return-previous-data-for-better-ux.
       */
      const newVersionsMap = Object.fromEntries<VersionBranchesData>(
        Object.entries(versionsMap).map(([version, data]) => [
          version,
          {
            ...data,
            // if the new data is null but we have previous data for this
            // version, return the previous branches data
            branches: data.branches ?? prevVersionsMap[version]?.branches ?? null,
            error: data.error || null,
          },
        ]),
      );
      const nextState = {
        canUserMerge,
        error,
        versionsMap: { ...prevVersionsMap, ...newVersionsMap },
      };

      // Only continue with a state update if there are changed values. This
      // quiets down the redux devtools action logs to only contain actions
      // that contain differences.
      const hasChanges = Object.entries(nextState).some(([key, value]) => {
        return JSON.stringify(value) !== JSON.stringify(get().branches[key]);
      });
      if (!hasChanges) return;

      set(
        state => {
          const writableVersionsMap = nextState.versionsMap as WritableDeep<typeof nextState.versionsMap>;
          const writableError = nextState.error as WritableDeep<typeof nextState.error>;
          state.branches.versionsMap = writableVersionsMap;
          state.branches.error = writableError;
          state.branches.canUserMerge = canUserMerge;

          // When running on the server, we must avoid marking this store as
          // "ready" to ensure it continues receiving updates until it gets
          // initialized on the client's first render.
          state.branches.isReady = isClient;
        },
        false,
        actionLog('branches.initialize', { versionsMap, error }),
      );
    },

    isReviewReportStale: (reviewReport = get().branches.reviewReport) => {
      const currentBranch = get().getCurrentChildBranch();
      return (
        !!currentBranch && !!reviewReport && new Date(reviewReport.created_at) < new Date(currentBranch.updated_at)
      );
    },

    renameBranch: async (name, newName) => {
      const { version } = parseVersionBranch(name);

      const request = fetcher<ReadBranchType>(get().getApiBaseUrl({ branch: name }), {
        method: 'PATCH',
        body: JSON.stringify({ name: newName }),
      });

      const {
        branches: { versionsMap },
      } = get();

      await mutate(
        versionsMap[version]?.swrKey,
        request.then(({ data }) =>
          produce(versionsMap[version]?.branches, draft => {
            const target = draft?.data.find(v => v.name === name);
            if (target) {
              target.name = data.name;
              target.uri = data.uri;
            }
          }),
        ),
        { revalidate: true },
      );

      // Update `currentBranch` if that branch was renamed.
      const { currentBranch } = get();
      const renamedCurrentBranch = currentBranch.name === name;
      if (renamedCurrentBranch) {
        set(
          state => {
            state.currentBranch.name = newName;
            state.currentBranch.uri = currentBranch.uri.replace(currentBranch.name, newName);
          },
          false,
          actionLog('branches.renameBranch.fulfilled', { name, newName }),
        );
      }

      return request;
    },

    revalidate: (versionName: string) => {
      const {
        branches: { versionsMap },
      } = get();

      return mutate<ReadBranchCollectionType>(versionsMap[versionName]?.swrKey || null);
    },

    revalidateCurrentBranch: () => {
      const { currentBranch, getApiBaseUrl } = get();
      return fetcher<ReadBranchType>(getApiBaseUrl({ branch: currentBranch.name }), { method: 'GET' }).then(res => {
        set(
          state => {
            state.currentBranch = res.data;
          },
          false,
          actionLog('branches.revalidateCurrentBranch', currentBranch?.name),
        );

        return res;
      });
    },

    updateEphemeralBranch: ephemeralBranch => {
      set(
        state => {
          state.branches.ephemeralBranch = ephemeralBranch === null ? null : createEphemeralBranch(ephemeralBranch);
        },
        false,
        actionLog('branches.updateEphemeralBranch', ephemeralBranch?.base),
      );
    },

    updateDiffStatusMap: diffStatusMap => {
      /**
       * Normalized data that further segments the provided args into additional
       * sub-sections. For example, segmenting API definitions out of the
       * reference section.
       */
      const nextData = produce<SuperHubBranchesSliceState['diffStatusMap']>(diffStatusMap, draft => {
        // Segment API definitions from reference.
        if (draft.reference) {
          draft.apiDefinitions = { ...draft.reference };

          if (draft.apiDefinitions) {
            draft.apiDefinitions.data = reduceDiffStatusData('apiDefinition', draft.reference);
            draft.reference.data = reduceDiffStatusData('reference', draft.reference);
          }
        }

        // Segment MDX components and reusable content from custom content.
        if (draft.customContent) {
          draft.customComponents = { ...draft.customContent };

          if (draft.customComponents) {
            draft.customComponents.data = reduceDiffStatusData('customComponent', draft.customContent);
            draft.customContent.data = reduceDiffStatusData('customContent', draft.customContent);
          }
        }
      });

      set(
        state => {
          Object.entries(nextData).forEach(([key, diffStatus]) => {
            // Required type-casting here b/c it gets lost.
            const section = key as keyof typeof diffStatusMap;

            // Preserve previous data that's been set so we don't lose it when
            // a section revalidates and goes back to a loading state.
            const preservedData = diffStatus.data || state.branches.diffStatusMap[section]?.data || null;
            const preservedSwrKey = diffStatus.swrKey || get().branches.diffStatusMap[section]?.swrKey || null;

            state.branches.diffStatusMap[section] = {
              data: preservedData,
              error: (diffStatus.error || null) as WritableDeep<typeof diffStatus.error>,
              // Before a section is connected to an SWR endpoint, make this an
              // additional loading state since there is no data to show.
              isLoading: diffStatus.isLoading || !preservedSwrKey,
              swrKey: preservedSwrKey as WritableDeep<typeof preservedSwrKey>,
            };
          });
        },
        false,
        actionLog('branches.updateDiffStatusMap', diffStatusMap),
      );
    },

    updateReviewReport: async reviewReport => {
      set(
        state => {
          state.branches.reviewReport = reviewReport;
        },
        false,
        actionLog('branches.updateReviewReport', reviewReport),
      );

      const reportSections = new Set<ReviewReportFileSection>();
      reviewReport.files.forEach(({ file }) => reportSections.add(file.section));

      // for each section that we have report results for, invalidate that
      // section's diff sidebar data so we get fresh per-page report stats
      reportSections.forEach(async reportSection => {
        const diffStatusSection = reviewReportSectionToBranchDiffSection[reportSection];
        const diffStatus = get().branches.diffStatusMap[diffStatusSection];

        await mutate(diffStatus?.swrKey);
      });
    },

    updateReviewStatus: async ({ branch = get().currentBranch.name, status }) => {
      const {
        branches: { versionsMap },
        currentBranch,
      } = get();

      const { version: parsedVersion, branch: parsedBranch } = parseVersionBranch(branch);
      if (!parsedBranch) {
        // For some reason, the branch name passed into this action does not
        // look like a valid branch. Return a rejected promise for this.
        throw new Error(`Invalid branch: "${branch}"`);
      }

      set(
        state => {
          const now = new Date().toISOString();
          const nextReviewStatus = {
            ready_at: status === 'ready' ? now : null,
            ready_by: {
              email: status === 'ready' ? state.user.email : null,
              name: status === 'ready' ? state.user.name : null,
            },
            status,
            updated_at: now,
          } satisfies Partial<ReadBranchReviewStatusType['data']>;

          // Update `currentBranch` if that branch was updated.
          if (currentBranch.name === parsedBranch && isBranch(state.currentBranch)) {
            state.currentBranch.review = {
              ...state.currentBranch.review,
              ...nextReviewStatus,
            };
            state.currentBranch.updated_at = nextReviewStatus.updated_at;
          }

          // Update versions map branch if branch exists.
          const foundBranch = state.branches.versionsMap[parsedVersion]?.branches?.data.find(
            vb => vb.name === parsedBranch,
          );
          if (foundBranch) {
            foundBranch.review = {
              ...foundBranch.review,
              ...nextReviewStatus,
            };
            foundBranch.updated_at = nextReviewStatus.updated_at;
          }
        },
        false,
        actionLog('branches.updateReviewStatus', { branch, status }),
      );

      // Send request to update review status.
      const request = fetcher<ReadBranchReviewStatusType>(`${get().getApiBaseUrl({ branch: parsedBranch })}/review`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });

      await mutate(
        versionsMap[parsedVersion]?.swrKey,
        request.then(async ({ data: reviewStatus }) => {
          set(
            state => {
              // Update `currentBranch` again now that we have the response.
              if (currentBranch.name === parsedBranch && isBranch(state.currentBranch)) {
                state.currentBranch.review = reviewStatus;
              }
            },
            false,
            actionLog('branches.updateReviewStatus.fulfilled', { branch, status }),
          );

          // Update versions map branch again now that we have the response.
          return produce(versionsMap[parsedVersion]?.branches, draft => {
            const target = draft?.data.find(b => b.name === parsedBranch);
            if (target) {
              target.review = reviewStatus;
            }
          });
        }),
        { revalidate: false },
      );

      return request;
    },
  },
});

export * from './Initialize';
export * from './InitializeDiffStatus';
export * from './InitializeReviewReport';
